<script setup>
import moment from 'moment-timezone'
import { timingText } from '@/plugins/misc'
import { useUsersStore, useReservationsStore, useRestaurantsStore, useUserGroupsStore } from '@/stores'

const selectedMarketBookings = ref('')

const restaurantsStore = useRestaurantsStore()
const usersStore = useUsersStore()

const { myUser } = storeToRefs(usersStore)
const {
  bookedRestaurants,
  viewingDetailsRestaurant,
  viewingDetailsRestaurantTab,
  citySelectorData,
} = storeToRefs(restaurantsStore)

const router = useRouter()

// Check if user is logged in (not a dummy/guest user)
const isUserLoggedIn = computed(() => {
  return myUser.value && myUser.value.userLevel !== 'dummy'
})

onBeforeMount(async () => {
  // Redirect non-logged-in users to discover page
  if (!isUserLoggedIn.value) {
    router.push('/discover')
    
    return
  }

  // 1. Always fetch the list of cities for the dropdown first
  await restaurantsStore.getCitySelectorData()

  // 2. NOTE: Skip heavy getRestaurants() call - GoogleImage will use bookedRestaurants data

  // 3. Check if a city was previously selected and stored
  const savedCity = localStorage.getItem('selectedCity')
  if (savedCity) {
    // 4. If so, set the dropdown to that city...
    selectedMarketBookings.value = savedCity

    // 5. ...and fetch the bookings for that specific city
    await restaurantsStore.getBookedRestaurants(null, false, savedCity)
  } else {
    // 6. If no city was saved, fetch the default "All Cities" view
    await restaurantsStore.getBookedRestaurants(null, false, null)
  }

  // 7. Set initial title based on the loaded city
  updateTitles(savedCity)
})

const newDraft = async item => {
  const restaurant = item.raw || item
  await usersStore.showLoadingOverlay()

  // Extract restaurant name - could be in name, confirmedRestaurant, preferredRestaurant, or requestedRestaurantsArray[0]
  const restaurantName = restaurant.name || 
                         restaurant.confirmedRestaurant || 
                         restaurant.preferredRestaurant || 
                         (restaurant.requestedRestaurantsArray && restaurant.requestedRestaurantsArray.length > 0 ? restaurant.requestedRestaurantsArray[0] : null)
  const market = restaurant.market
  
  if (!restaurantName || !market) {
    console.error('Missing restaurant name or market:', { restaurant, item })
    await usersStore.hideLoadingOverlay()
    
    return
  }
  
  try {
    if (!myUser.value) {
      await usersStore.loginNewDummyUser()
    }
    await usersStore.startNewReservation({ restaurant: restaurantName, market })
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

const showRestaurantDetails = async (marketName, restaurantName) => {
  // First try to find the full restaurant data from main store (has acceptingReferrals, etc.)
  const { restaurants } = storeToRefs(restaurantsStore)
  let fullRestaurant = restaurants.value?.find(r => r.name == restaurantName && r.market == marketName)

  if (!fullRestaurant) {
    // Load restaurants store if not already loaded to get complete data
    await restaurantsStore.getRestaurants(null, false, marketName)
    fullRestaurant = restaurants.value?.find(r => r.name == restaurantName && r.market == marketName)
  }

  if (fullRestaurant) {
    // Use complete data with acceptingReferrals and all fields
    viewingDetailsRestaurant.value = fullRestaurant
  } else {
    // Fallback to .raw data if not found in main store
    let bookingRestaurant = bookedRestaurants.value.find(r => r.raw.name == restaurantName && r.raw.market == marketName)
    if (bookingRestaurant) {
      viewingDetailsRestaurant.value = bookingRestaurant.raw
    }
  }
  viewingDetailsRestaurantTab.value = 'Details'
}

const selectableMarketsJustBooked = computed(() => {
  return citySelectorData.value?.cities || []
})

const filteredJustBookedReservations = computed(() => {
  if (bookedRestaurants.value) {
    // Backend already filters by market, so just sort the results
    let returnArray = [...bookedRestaurants.value]

    returnArray.sort( function(a,b){
      if (a.sortDateTimeBooking < b.sortDateTimeBooking) {
        return 1
      }
      if (a.sortDateTimeBooking> b.sortDateTimeBooking) {
        return -1
      }
      
      return 0
    })
    
    return returnArray
  } else {
    return []
  }
})

const backHome = computed(() => {
  if (myUser?.value && myUser?.value?.userLevel != 'dummy') {
    return '/discover'
  }
  
  return '/home/'
})

// Dynamic title management
const pageTitle = ref('Booked') // Default to "Booked"
const layoutDynamicTitle = inject('dynamicPageTitle', null)

// Helper function to update titles based on city selection
const updateTitles = city => {
  const isAllCities = !city
  const newTitle = isAllCities ? 'Booked' : 'Recent Member Bookings'
  pageTitle.value = newTitle

  // Update the layout's dynamic title for mobile navbar
  if (layoutDynamicTitle) {
    layoutDynamicTitle.value = newTitle
  }
}

// Watcher for city dropdown changes (data fetching and title updates)
watch(selectedMarketBookings, async newCity => {
  // Handle city dropdown changes (data fetching)
  localStorage.setItem('selectedCity', newCity || '')
  await restaurantsStore.getBookedRestaurants(null, false, newCity || null)

  // Handle title updates
  updateTitles(newCity)
})

// Helper function to calculate time since booking was accepted
const getTimeSinceBooked = item => {
  const acceptedAt = item.acceptedAt
  if (!acceptedAt) return ''
  
  const now = moment()
  const bookedTime = moment(acceptedAt)
  const diffMinutes = now.diff(bookedTime, 'minutes')
  
  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }
  
  const diffHours = now.diff(bookedTime, 'hours')
  if (diffHours < 24) {
    return `${diffHours}h`
  }
  
  const diffDays = now.diff(bookedTime, 'days')
  if (diffDays <= 30) {
    return `${diffDays}d`
  }
  
  const diffMonths = now.diff(bookedTime, 'months')
  if (diffMonths <= 11) {
    return `${diffMonths}mo`
  }
  
  const diffYears = now.diff(bookedTime, 'years')
  
  return `${diffYears}yr`
}
</script>

<template>
  <VContainer fluid>
    <div
      class="d-flex flex-column"
      :class="{
        'justify-center align-start': !$vuetify.display.smAndDown,
        'justify-center align-center': $vuetify.display.smAndDown,
      }"
    >
      <div
        v-if="!$vuetify.display.smAndDown"
        class="d-flex align-center mb-4 w-100"
      >
        <RouterLink :to="backHome">
          <VIcon
            color="secondary"
            size="x-large"
            class="mr-4"
          >
            mdi-chevron-left
          </VIcon>
        </RouterLink>
        <h1 class="align-center text-center text-lg">
          {{ pageTitle }}
        </h1>
      </div>
      <VAutocomplete
        v-model="selectedMarketBookings"
        label="City"
        :items="selectableMarketsJustBooked"
        item-title="text"
        item-value="value"
        no-data-text="No Matches"
        clearable
        clear-on-select
        density="compact"
        :width="!$vuetify.display.smAndDown ? '50%' : '100%'"
      >
        <template #message="{ message }">
          <p class="dark-text">
            {{ message }}
          </p>
        </template>
      </VAutocomplete>
    </div>
    <VRow v-if="!$vuetify.display.smAndDown">
      <div class="d-flex flex-wrap align-center justify-start w-100">
        <VVirtualScroll
          :key="`bookings-desktop-${selectedMarketBookings || 'all'}-${filteredJustBookedReservations.length}`"
          :items="filteredJustBookedReservations"
          max-height="600"
          item-height="98"
        >
          <template #default="{ item }">
            <VListItem @click="showRestaurantDetails(item.raw.market, item.raw.name)">
              <div class="d-flex flex-row justify-start align-center">
                <div class="restaurant-image-holder--mobile flex-shrink-0">
                  <GoogleImage
                    class="restaurant-image"
                    :restaurant="item.raw"
                  />
                </div>
                <div class="pa-4">
                  <VListItemTitle>
                    <div class="d-flex flex-wrap align-items-center">
                      <div>
                        <VIcon
                          v-if="item.raw.status === 'Preferred'"
                          style="color: rgb(var(--v-theme-primary));"
                        >
                          iconamoon:certificate-badge-light
                        </VIcon>
                        <span
                          class="font-weight-bold"
                          :style="(item.status || item.raw.status) === 'Preferred' ? 'color: rgb(var(--v-theme-primary));' : 'margin-left: .5rem;'"
                        >
                          {{ item.raw.name }}
                        </span>
                        <span class="trending-name mt-1 ml-1">
                          <RestaurantHeart
                            :restaurant-name="item.raw.name"
                            :restaurant-market="item.raw.market"
                          />
                        </span>
                        <span
                          v-if="getTimeSinceBooked(item)"
                          class="time-since-booked ml-1"
                        >
                          {{ getTimeSinceBooked(item) }}
                        </span>
                      </div>
                    </div>
                  </VListItemTitle>
                  <VListItemSubtitle
                    :style="(item.status || item.raw.status) === 'Preferred' ? 'margin-left: 24px; color: black;' : 'color: black; margin-left: 24px;'"
                  >
                    <div class="content">
                      <span
                        v-if="item.raw.group"
                        class="trending-company text-xs"
                      >{{ item.raw.group }}</span>
                      <span class="trending-company text-xs">
                        {{ item.raw.market.split(',')[0].trim() }}
                      </span>
                      <span
                        v-if="item.date"
                        class="trending-company"
                      >
                        {{ moment(item.date + ' ' + (item.confirmedTime ? item.confirmedTime : item.preferredTime), 'YYYY-MM-DD hh:mm:a').format("ddd") }} |
                        <VIcon
                          icon="mdi-account-multiple-outline"
                          size="16"
                        />
                        {{ item.partySize }} |
                        {{ moment(item.date + ' ' + (item.confirmedTime ? item.confirmedTime : item.preferredTime), 'YYYY-MM-DD hh:mm:a').format("h:mma") }}
                      </span>
                      <span
                        v-if="item.reservationType == 'general'"
                        class="trending-company"
                      >
                        <VIcon
                          style="margin-bottom:2px;"
                          icon="mdi-check"
                          size="16"
                        />
                        {{ timingText(item) }} | {{ item.occasion }}
                      </span>
                    </div>
                  </VListItemSubtitle>
                </div>
              </div>
              <template #append>
                <VBtn
                  size="small"
                  density="compact"
                  color="primary"
                  style="padding-left: 5px !important; padding-right: 5px !important;"
                  icon="mdi-plus"
                  @click.stop="newDraft(item);"
                />
              </template>
            </VListItem>
            <VDivider inset />
          </template>
        </VVirtualScroll>
      </div>
    </VRow>
    <VRow v-else>
      <VCol
        cols="12"
        class="trending-wrapper"
      >
        <VVirtualScroll
          :key="`bookings-mobile-${selectedMarketBookings || 'all'}-${filteredJustBookedReservations.length}`"
          :items="filteredJustBookedReservations"
          max-height="600"
          item-height="98"
        >
          <template #default="{ item }">
            <VListItem @click="showRestaurantDetails(item.raw.market, item.raw.name)">
              <div class="d-flex flex-row justify-start align-center">
                <div class="restaurant-image-holder--mobile flex-shrink-0">
                  <GoogleImage
                    class="restaurant-image"
                    :restaurant="item.raw"
                  />
                </div>
                <div class="py-4 pl-4">
                  <VListItemTitle>
                    <div class="d-flex flex-wrap justify-start align-items-center">
                      <div class="d-flex align-start">
                        <VIcon
                          v-if="item.raw.status === 'Preferred'"
                          style="color: rgb(var(--v-theme-primary));"
                        >
                          iconamoon:certificate-badge-light
                        </VIcon>
                        <span
                          class="text-truncate"
                          :style="item.raw.status === 'Preferred' ? 'color: rgb(var(--v-theme-primary));' : 'margin-left: .5rem;'"
                          style="max-width: 150px;"
                        >
                          <span class="font-weight-bold">
                            {{ item.raw.name }}
                          </span>
                        </span>
                        <span class="trending-name mt-1">
                          <RestaurantHeart
                            :restaurant-name="item.raw.name"
                            :restaurant-market="item.raw.market"
                          />
                        </span>
                        <span
                          v-if="getTimeSinceBooked(item)"
                          class="time-since-booked ml-1"
                        >
                          {{ getTimeSinceBooked(item) }}
                        </span>
                      </div>
                    </div>
                  </VListItemTitle>
                  <VListItemSubtitle :style="item.raw.status === 'Preferred' ? 'margin-left: 24px; color: black;' : 'color: black; margin-left: 24px;'">
                    <div class="content">
                      <span
                        v-if="item.raw.group"
                        class="trending-company text-xs"
                      >{{ item.raw.group }}</span>
                      <span class="trending-company text-xs">
                        {{ item.raw.market.split(',')[0].trim() }}
                      </span>
                      <span
                        v-if="item.date"
                        class="trending-company"
                      >
                        {{ moment(item.date + ' ' + (item.confirmedTime ? item.confirmedTime : item.preferredTime), 'YYYY-MM-DD hh:mm:a').format("ddd") }} |
                        <VIcon
                          icon="mdi-account-multiple-outline"
                          size="16"
                        />
                        {{ item.partySize }} |
                        {{ moment(item.date + ' ' + (item.confirmedTime ? item.confirmedTime : item.preferredTime), 'YYYY-MM-DD hh:mm:a').format("h:mma") }}
                      </span>
                      <span
                        v-if="item.reservationType == 'general'"
                        class="trending-company"
                      >
                        <VIcon
                          style="margin-bottom:2px;"
                          icon="mdi-check"
                          size="16"
                        />
                        {{ timingText(item) }} | {{ item.occasion }}
                      </span>
                    </div>
                  </VListItemSubtitle>
                </div>
              </div>
              <template #append>
                <VBtn
                  size="small"
                  density="compact"
                  style="padding-left: 5px !important; padding-right: 5px !important;"
                  icon="mdi-plus"
                  @click.stop="newDraft(item);"
                />
              </template>
            </VListItem>
            <VDivider inset />
          </template>
        </VVirtualScroll>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style lang="scss" scoped>
.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none; /* Prevent dragging images */
}

.restaurant-image:hover.lower-on-hover {
  width: 90%;
  height: 90%;
}

.restaurant-image-holder {
  width: 100%;
  height: 50%;
}

.restaurant-image-holder--mobile {
  width: 4rem;
  height: 4rem;
}

.dark-text {
  color: black !important;
  font-weight: 400 !important;
}

.time-since-booked {
  font-family: var(--ds-font-body);
  font-size: 11px;
  font-weight: 400;
  color: var(--ds-text-muted);
  white-space: nowrap;
}

// Ensure consistent layout at all breakpoints
@media (max-width: 1279px) {
  .v-container.v-container--fluid {
    max-width: 100%;
  }
  
  .v-row {
    margin: 0 -12px;
  }
}

@media (max-width: 960px) {
  .v-container.v-container--fluid {
    padding: 12px;
  }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
